import { createContext, useContext, useMemo, useState, FC, ReactNode } from "react";

export enum InviteNewUserStepEnum {
  AcceptOrRejectInvitation = 0,
  CompleteRegistration = 1,
  RegistrationComplete = 2
}

interface InviteNewUserFlowContextProps {
  currentStep: InviteNewUserStepEnum;
  setCurrentStep: (step: InviteNewUserStepEnum) => void;
  email: string | null;
  setEmail: (email: string | null) => void;
  workflowId: string | null;
  setWorkflowId: (workflowId: string | null) => void;
  organizationId: string | null;
  setOrganizationId: (organizationId: string | null) => void;
  organizationName: string | null;
  setOrganizationName: (organizationName: string | null) => void;
  language: string | null;
  setLanguage: (language: string | null) => void;
  relationType: string | null;
  setRelationType: (relationType: string | null) => void;
  invitationState: string | null;
  setInvitationState: (invitationState: string | null) => void;
  resetFlow: () => void;
}

const InviteNewUserFlowContext = createContext<InviteNewUserFlowContextProps | undefined>(
  undefined
);

export const useInviteNewUserFlow = () => {
  const ctx = useContext(InviteNewUserFlowContext);
  if (!ctx)
    throw new Error("useInviteNewUserFlow must be used within an InviteNewUserFlowProvider");
  return ctx;
};

export const InviteNewUserFlowProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<InviteNewUserStepEnum>(
    InviteNewUserStepEnum.AcceptOrRejectInvitation
  );
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [relationType, setRelationType] = useState<string | null>(null);
  const [invitationState, setInvitationState] = useState<string | null>(null);

  const resetFlow = () => {
    setCurrentStep(InviteNewUserStepEnum.AcceptOrRejectInvitation);
    setWorkflowId(null);
    setOrganizationId(null);
    setOrganizationName(null);
    setLanguage(null);
    setEmail(null);
    setRelationType(null);
    setInvitationState(null);
  };

  const value = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      workflowId,
      setWorkflowId,
      organizationId,
      setOrganizationId,
      organizationName,
      setOrganizationName,
      email,
      setEmail,
      language,
      setLanguage,
      relationType,
      setRelationType,
      invitationState,
      setInvitationState,
      resetFlow
    }),
    [
      currentStep,
      workflowId,
      organizationId,
      organizationName,
      language,
      email,
      relationType,
      invitationState
    ]
  );

  return (
    <InviteNewUserFlowContext.Provider value={value}>{children}</InviteNewUserFlowContext.Provider>
  );
};
